import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// 定义音色类型
export interface Voice {
  voice_id: string;
  name: string;
  audio_url?: string;
  voice_type?: string;
  gender?: string;
  language?: string;
  description?: string;
  sample_rate?: number;
  is_streaming?: boolean;
}

// 定义VoiceSelector的属性接口
interface VoiceSelectorProps {
  voices: Voice[];
  selectedVoice: string;
  onVoiceSelect: (voiceId: string) => void;
  voiceSpeed: number;
  onSpeedChange: (speed: number) => void;
}

const VoiceSelector: React.FC<VoiceSelectorProps> = ({
  voices,
  selectedVoice,
  onVoiceSelect,
  voiceSpeed,
  onSpeedChange
}) => {
  // 音频播放状态管理
  const [currentPlayingVoice, setCurrentPlayingVoice] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const { t } = useTranslation();

  // 组件卸载时清理音频
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, []);

  // 播放音频
  const playAudio = (voiceId: string, audioUrl?: string) => {
    if (!audioUrl) return;
    // 停止当前播放的音频
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    // 创建新的音频实例
    const audio = new Audio(audioUrl.trim().replace(/`/g, ''));
    audioRef.current = audio;

    // 设置播放状态
    setCurrentPlayingVoice(voiceId);
    setIsPlaying(true);

    // 播放结束时重置状态
    audio.onended = () => {
      setIsPlaying(false);
      setCurrentPlayingVoice(null);
    };

    // 播放音频
    audio.play().catch(error => {
      console.error('播放音频失败:', error);
      setIsPlaying(false);
      setCurrentPlayingVoice(null);
    });
  };

  // 停止音频
  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
    setCurrentPlayingVoice(null);
  };

  return (
    <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-lg p-4 mb-4 transition-all duration-300 opacity-100">
      {/* 标题 */}
      <h3 className="text-sm font-medium text-white mb-3">{t('inputBar.voiceConfig.title')}</h3>

      {/* 语速小标题 */}
      <label className="block text-xs font-medium text-gray-300 mb-1">{t('inputBar.voiceConfig.speed')}</label>

      {/* 语速滑块 */}
      <div className="bg-gray-800/95 backdrop-blur-sm border border-gray-700/50 rounded-lg p-3 mb-3">
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400">0.5x</span>
          <input
            type="range"
            min="0.5"
            max="2.0"
            step="0.1"
            value={voiceSpeed}
            onChange={(e) => onSpeedChange(parseFloat(e.target.value))}
            className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
          />
          <span className="text-xs text-gray-400">2.0x</span>
          <span className="text-sm font-medium text-blue-400 w-12 text-right">{voiceSpeed.toFixed(1)}x</span>
        </div>
      </div>

      {/* 音色小标题 */}
      <label className="block text-xs font-medium text-gray-300 mb-1">{t('inputBar.voiceConfig.voice')}</label>

      <div className="bg-gray-800/95 backdrop-blur-sm border border-gray-700/50 rounded-lg p-2 max-h-60 overflow-y-auto custom-scrollbar">
        {voices.length > 0 ? (
          voices.map(voice => (
            <div
              key={voice.voice_id}
              className={`flex items-center justify-between py-2 px-3 rounded-lg mb-2 transition-colors duration-200 ${selectedVoice === voice.voice_id ? 'border border-blue-500/50' : 'hover:bg-gray-700/50 cursor-pointer'} active:bg-transparent active:outline-none active:border-transparent focus:bg-transparent focus:outline-none focus:border-transparent focus-visible:bg-transparent focus-visible:outline-none focus-visible:border-transparent -webkit-tap-highlight-color: transparent`}
              onClick={() => onVoiceSelect(voice.voice_id)}
            >
              {/* 左侧：音色名称 */}
              <div className="flex-1 text-white text-sm">
                {voice.name}
              </div>

              {/* 右侧：试听按钮 */}
              <button
                className={`flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 ${currentPlayingVoice === voice.voice_id && isPlaying ? 'bg-pink-500/50 hover:bg-pink-500/70' : 'bg-gray-700/50 hover:bg-gray-600/50'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (currentPlayingVoice === voice.voice_id && isPlaying) {
                    stopAudio();
                  } else {
                    playAudio(voice.voice_id, voice.audio_url);
                  }
                }}
              >
                {currentPlayingVoice === voice.voice_id && isPlaying ? (
                  <Pause className="w-4 h-4 text-white" />
                ) : (
                  <Play className="w-4 h-4 text-white" />
                )}
              </button>
            </div>
          ))
        ) : (
          <div className="p-3 text-gray-400 text-center">
            {t('inputBar.voiceConfig.loading')}
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceSelector;
